import { Alert, Autocomplete, Button, Checkbox, FormControl, FormControlLabel, FormGroup, Grid, TextField } from '@mui/material';
import { FormEvent, SyntheticEvent, useContext, useState } from 'react';
import { Store } from '../../../../app/services/api/generated';
import { useTranslation } from 'react-i18next';
import { UserContext } from '../../../../components/shared/useUser';
import useNswagClient from '../../../../hooks/api/useNswagClient';
import DatePickerLocalized from '../../../../components/shared/DatePickerLocalized';
import { filterStoreOptions } from '../../../../utils';

type IProps = {
  onClose?: () => void;
}

const OutstandingDeliveriesReportForm = (props: IProps) => {
  const { user, selectedStore } = useContext(UserContext);
  const [selectedStores, setSelectedStores] = useState<Store[]>([]);
  const [fromDate, setFromDate] = useState<string>('');
  const [toDate, setToDate] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [successMessage, setSuccessMessage] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState<boolean>(false);
  const [isRequestSubmitted, setIsRequestSubmitted] = useState<boolean>(false);
  const [checkedAllStores, setCheckedAllStores] = useState<boolean>(false);
  const { runOutstandingDeliveriesReport } = useNswagClient();
  const { t } = useTranslation('common');
  const { t: datepickerLabels } = useTranslation('orderHistory');

  const currentFranchiseStores = user?.stores?.filter(x => x.franchiseId === selectedStore?.franchiseId) ?? [];

  const handleSelectAllChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const checked = event.target.checked;
    setSelectedStores([]);
    setCheckedAllStores(checked);
  };

  const handleSelectChange = (event: SyntheticEvent, value: Store[]) => {
    setSelectedStores(value);
  };

  const getMinDate = () => {
    return new Date('1900-01-01').toISOString().split('T')[0];
  };

  const getMaxDate = () => {
    return new Date().toISOString().split('T')[0];
  };

  const handleSubmit = (event: FormEvent) => {
    event.preventDefault();
    setIsGenerating(true);
    setErrorMessage('');
    setSuccessMessage('');

    const storeNumbers = checkedAllStores
      ? currentFranchiseStores.map(x => x.storeNumber as string)
      : selectedStores.map(x => x.storeNumber as string);

    const selectedAllStores = checkedAllStores || storeNumbers.length === currentFranchiseStores.length;

    const request = {
      from: fromDate,
      to: toDate,
      stores: selectedAllStores ? [] : storeNumbers,
      reportOnAllAvailableStores: selectedAllStores,
    };

    runOutstandingDeliveriesReport(request).then(() => {
      setSuccessMessage(t('reporting.generic.post-generation-message'));
      setIsRequestSubmitted(true);
    }).catch((error) => {
      if (typeof(error) === 'string') {
        setErrorMessage(error);
      }
      console.log(error);
    }).finally(() => {
      setIsGenerating(false);
    });
  };

  return (
    <form onSubmit={handleSubmit}>
      <Grid container
        spacing={10}
        sx={{ pt: '10px' }}>
        <Grid item
          sm={6}
          xs={12}>
          <FormControl fullWidth>
            <Autocomplete
              fullWidth
              multiple
              limitTags={2}
              options={currentFranchiseStores}
              value={selectedStores}
              onChange={handleSelectChange}
              disabled={checkedAllStores}
              getOptionKey={(option) => option.storeNumber ?? ''}
              getOptionLabel={(option) => option.storeName ?? ''}
              filterOptions={filterStoreOptions}
              renderInput={(params) => (
                <TextField {...params}
                  required={selectedStores.length === 0 && !checkedAllStores}
                  label={t('reporting.select-stores')}
                  placeholder={t('reporting.select-stores')} />
              )}
            />
          </FormControl>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <FormGroup style={{ textAlign: 'right' }}>
            <FormControlLabel
              control={<Checkbox
                checked={checkedAllStores}
                onChange={handleSelectAllChange} />}
              label={t('reporting.all-stores')}
            />
          </FormGroup>
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <DatePickerLocalized
            label={t('reporting.start-date')}
            value={fromDate}
            onChange={(newValue) => setFromDate(newValue ?? '')}
            useCalendar={false}
            shouldDisableDate={(date: string) =>
              date < getMinDate() || date > (toDate ? toDate : getMaxDate())
            }
            translations={{
              clearButtonLabel: datepickerLabels('calendar.clearButtonLabel'),
              todayButtonLabel: datepickerLabels('calendar.todayButtonLabel'),
              cancelButtonLabel: datepickerLabels('calendar.cancelButtonLabel'),
              okButtonLabel: datepickerLabels('calendar.okButtonLabel'),
              datePickerToolbarTitle: t('reporting.start-date'),
            }}
          />
        </Grid>
        <Grid item
          sm={6}
          xs={12}>
          <DatePickerLocalized
            label={t('reporting.end-date')}
            value={toDate}
            onChange={(newValue) => setToDate(newValue ?? '')}
            useCalendar={false}
            shouldDisableDate={(date: string) =>
              date < (fromDate ? fromDate : getMinDate()) || date > getMaxDate()
            }
            translations={{
              clearButtonLabel: datepickerLabels('calendar.clearButtonLabel'),
              todayButtonLabel: datepickerLabels('calendar.todayButtonLabel'),
              cancelButtonLabel: datepickerLabels('calendar.cancelButtonLabel'),
              okButtonLabel: datepickerLabels('calendar.okButtonLabel'),
              datePickerToolbarTitle: t('reporting.end-date'),
            }}
          />
        </Grid>
        {
          errorMessage &&
          <Grid item
            xs={12}>
            <Alert severity="error">{errorMessage}</Alert>
          </Grid>
        }
        {
          successMessage &&
          <Grid item
            xs={12}>
            <Alert severity="success">{successMessage}</Alert>
          </Grid>
        }
        <Grid item
          xs={12}
          sx={{ display: 'flex', justifyContent: 'right', columnGap: '10px' }}>
          <Button size='lg'
            variant='secondary'
            onClick={props.onClose}>{t('cancel')}</Button>
          <Button size='lg'
            disabled={
              isGenerating ||
              isRequestSubmitted ||
              !fromDate ||
              !toDate ||
              (!checkedAllStores && selectedStores.length === 0)
            }
            type='submit'>{t('reporting.generate')}</Button>
        </Grid>
      </Grid>
    </form>
  );
};

export default OutstandingDeliveriesReportForm;
